import AirTypeIcon from '@components/icons/CargoTypeIcons/AirTypeIcon';
import AutoTypeIcon from '@components/icons/CargoTypeIcons/AutoTypeIcon';
import ContainerTypeIcon from '@components/icons/CargoTypeIcons/ContainerIcon';
import CustomTypeIcon from '@components/icons/CargoTypeIcons/CustomTypeIcon';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { CargoType } from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/interfaces';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useDelivery from '../../useDelivery';

// Пропсы компонента
export type CargoTypeSelectProps = {
  cargoType: CargoType;
  onChangeCargoType: { (type: 'container' | 'custom' | 'auto' | 'air' | 'fclFtl'): void };
};

// Карта типа и ее иконки
const IconByTypeMap: {
  [k in 'container' | 'custom' | 'auto' | 'air' | 'fclFtl']: () => React.ReactNode;
} = {
  air: () => <AirTypeIcon />,
  auto: () => <AutoTypeIcon sx={{ fontSize: '18px!important' }} />,
  container: () => <ContainerTypeIcon />,
  custom: () => <CustomTypeIcon />,
  fclFtl: () => <></>,
};

/**
 * Компонент выбора типа отправления
 * @param props
 */
const CargoTypeSelect = (props: CargoTypeSelectProps) => {
  const { cargoType, onChangeCargoType } = props;
  const { t } = useTranslation('Common');
  const handlerChange = useCallback(
    (event: any) => {
      onChangeCargoType(event.target.value);
    },
    [onChangeCargoType]
  );

  const { useIsRoutesLoading, useIsLoading } = useDelivery();
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();

  const options: {
    type: 'container' | 'custom' | 'auto' | 'air' | 'fclFtl';
    label: React.ReactNode;
  }[] = [
    {
      type: 'container',
      label: 'FCL',
    },
    {
      type: 'custom',
      label: 'LCL/LTL',
    },
    {
      type: 'auto',
      label: 'FTL',
    },
    {
      type: 'air',
      label: 'AVIA',
    },
    {
      type: 'fclFtl',
      label: (
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ContainerTypeIcon />
            <span>FCL</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <svg
              className={'replace'}
              width="16"
              height="16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill={'#9199AA'}
                d="M4.4 7.6l-.8.8L.1 4.9a.5.5 0 010-.8L3.6.6l.8.8L1.7 4H16v1H1.7l2.7 2.6zM0 11h14.3l-2.7-2.6.8-.8 3.5 3.5c.1.2.1.6 0 .8l-3.5 3.5-.8-.8 2.7-2.6H0v-1z"
              />
            </svg>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>FTL</span>
            <AutoTypeIcon sx={{ fontSize: '18px!important' }} />
          </div>
        </div>
      ),
    },
  ];

  const GetIcon = (type: 'container' | 'custom' | 'auto' | 'air' | 'fclFtl') =>
    IconByTypeMap[type]();

  return (
    <>
      <FormControl variant="outlined" fullWidth={true}>
        <InputLabel id="cargo-type-select">{t('Common:Type')}</InputLabel>
        <Select
          value={cargoType || ''}
          MenuProps={{
            disableScrollLock: true,
            disablePortal: true,
          }}
          disabled={isRoutesLoading || isLoading}
          id={'cargo-type-select'}
          className={'cargo-type-select'}
          onChange={handlerChange}
          label={t('Common:Type')}
          defaultValue={options[0].type}
          renderValue={(value) => (
            <div className={'select-input'}>
              {GetIcon(value as any)}
              <span>{options.find((o) => o.type === value)?.label}</span>
            </div>
          )}
        >
          {options.map((option) => (
            <MenuItem value={option.type} key={option.type}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '16px 1fr',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                {GetIcon(option.type)}
                <span>{option.label}</span>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export default CargoTypeSelect;
